import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsApi, walletApi, MyStats, SavedBankDetails } from '../api';
import { User, Mail, Phone, Trophy, MapPin, Camera, Edit2, Save, X, LogOut, TrendingUp, Flame, Coins, Banknote } from 'lucide-react';
import Layout from '../components/Layout';
import BankAccountForm from '../components/BankAccountForm';
import { Card, Button, IconBadge } from '../components/ui';
import { formatHandicapIndex, HANDICAP_INDEX_ERROR, MAX_HANDICAP_INDEX, parseHandicapIndex } from '../utils/handicapIndex';

const Profile: React.FC = () => {
  const { user, logout, updateProfile, uploadAvatar } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [handicap, setHandicap] = useState(formatHandicapIndex(user?.handicap, ''));
  const [homeClub, setHomeClub] = useState(user?.home_club || '');
  const [facebookUrl, setFacebookUrl] = useState(user?.facebook_url || '');
  const [instagramUrl, setInstagramUrl] = useState(user?.instagram_url || '');
  const [xUrl, setXUrl] = useState(user?.x_url || '');
  const [tiktokUrl, setTiktokUrl] = useState(user?.tiktok_url || '');
  const [isSaving, setIsSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [stats, setStats] = useState<MyStats | null>(null);
  const [statsError, setStatsError] = useState('');
  const [bank, setBank] = useState<SavedBankDetails | null>(null);
  const [showBankForm, setShowBankForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadStats = async () => {
      setStatsError('');
      try {
        const data = await statsApi.getMyStats();
        if (data && typeof data.games_completed === 'number') {
          setStats(data);
        } else {
          setStats(null);
          setStatsError('Playing stats appear here when a real session is connected.');
        }
      } catch (err: any) {
        setStatsError(err.response?.data?.detail || 'Failed to load stats');
      }
    };

    loadStats();
    walletApi.getSavedBankDetails().then(setBank).catch(() => {});
  }, []);

  const maskedAccount = (n: string) => {
    const tail = n.length >= 4 ? n.slice(-4) : n;
    return `••••${tail}`;
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsSaving(true);
      const previewUrl = URL.createObjectURL(file);
      try {
        setAvatarUrl(previewUrl);
        const updated = await uploadAvatar(file);
        if (updated?.avatar_url) {
          setAvatarUrl(updated.avatar_url);
        }
      } catch (error) {
        console.error('Failed to upload avatar:', error);
      } finally {
        URL.revokeObjectURL(previewUrl);
        setIsSaving(false);
      }
    }
  };

  const handleSave = async () => {
    setProfileError('');
    const handicapIndex = handicap ? parseHandicapIndex(handicap) : undefined;
    if (handicap && handicapIndex === null) {
      setProfileError(HANDICAP_INDEX_ERROR);
      return;
    }
    setIsSaving(true);
    try {
      const payload: any = {
        full_name: fullName,
        phone: phone || undefined,
        handicap: handicapIndex,
        home_club: homeClub || undefined,
        facebook_url: facebookUrl || undefined,
        instagram_url: instagramUrl || undefined,
        x_url: xUrl || undefined,
        tiktok_url: tiktokUrl || undefined,
      };

      if (avatarUrl && !avatarUrl.startsWith('blob:')) {
        payload.avatar_url = avatarUrl;
      }

      await updateProfile(payload);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save profile:', error);
      setProfileError('We could not save your profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <Layout>
      <div className="pb-20 space-y-6">
        {/* Profile Header with Photo */}
        <Card className="bg-gradient-to-br from-brand-800 to-brand-950 text-white border border-accent-400/30 p-6 text-center relative overflow-hidden">
          {/* Edit Button */}
          {user && (
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition"
            >
              {isEditing ? <X className="h-5 w-5" /> : <Edit2 className="h-5 w-5" />}
            </button>
          )}

          {/* Avatar with Photo Upload */}
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="h-12 w-12" />
              )}
            </div>
            {isEditing && (
              <button
                onClick={handlePhotoClick}
                className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg text-emerald-600 hover:bg-gray-100 transition"
              >
                <Camera className="h-4 w-4" />
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
          </div>

          {isEditing ? (
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="text-2xl font-bold bg-white/20 rounded-lg px-4 py-2 text-center text-white placeholder-white/60 w-full max-w-xs"
              placeholder="Your Name"
            />
          ) : (
            <h1 className="text-2xl font-bold">{user?.full_name || 'Player profile'}</h1>
          )}
          <p className="opacity-80 mt-1">{user?.email || 'Your golf life, wins, and questionable decisions.'}</p>
        </Card>

        <Card padding="lg" className="p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Your Stats</h2>

          {statsError && (
            <div className="bg-[#f2ede2] text-brand-800 border border-[#ded3bc] p-3 rounded-xl text-sm mb-4">
              {statsError}
            </div>
          )}

          {!stats ? (
            statsError ? null : (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
              </div>
            )
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <IconBadge><TrendingUp /></IconBadge>
                  <span className="text-xs text-gray-500">Win rate</span>
                </div>
                <p className="text-2xl font-bold text-gray-800">{Math.round(stats.win_rate * 100)}%</p>
                <p className="text-xs text-gray-500">{stats.wins}/{stats.games_completed} completed</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <IconBadge tone="gold"><Flame /></IconBadge>
                  <span className="text-xs text-gray-500">Streak</span>
                </div>
                <p className="text-2xl font-bold text-gray-800">{stats.current_win_streak}</p>
                <p className="text-xs text-gray-500">wins in a row</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <IconBadge><Coins /></IconBadge>
                  <span className="text-xs text-gray-500">Winnings</span>
                </div>
                <p className="text-2xl font-bold text-gray-800">R{stats.total_winnings_zar.toFixed(0)}</p>
                <p className="text-xs text-gray-500">Biggest: R{stats.biggest_win_zar.toFixed(0)}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <IconBadge tone="gold"><Trophy /></IconBadge>
                  <span className="text-xs text-gray-500">Best round</span>
                </div>
                <p className="text-2xl font-bold text-gray-800">{stats.best_round_strokes ?? '—'}</p>
                <p className="text-xs text-gray-500">strokes</p>
              </div>
            </div>
          )}
        </Card>

        {user && <Card padding="lg" className="p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Profile Details</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Mail className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium text-gray-800">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Phone className="h-5 w-5 text-gray-500" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Phone</p>
                {isEditing ? (
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 mt-1 bg-white border border-gray-200 rounded-lg text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="+27 82 123 4567"
                  />
                ) : (
                  <p className="font-medium text-gray-800">{user?.phone || 'Not set'}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Trophy className="h-5 w-5 text-amber-500" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Handicap Index</p>
                {isEditing ? (
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    max={MAX_HANDICAP_INDEX}
                    value={handicap}
                    onChange={(e) => setHandicap(e.target.value)}
                    className="w-full px-3 py-2 mt-1 bg-white border border-gray-200 rounded-lg text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="11.1"
                  />
                ) : (
                  <p className="font-medium text-gray-800">
                    {formatHandicapIndex(user?.handicap, 'Not set')}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <MapPin className="h-5 w-5 text-gray-500" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Home Club</p>
                {isEditing ? (
                  <input
                    type="text"
                    value={homeClub}
                    onChange={(e) => setHomeClub(e.target.value)}
                    className="w-full px-3 py-2 mt-1 bg-white border border-gray-200 rounded-lg text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Randpark Golf Club"
                  />
                ) : (
                  <p className="font-medium text-gray-800">{user?.home_club || 'Not set'}</p>
                )}
              </div>
            </div>
          </div>
        </Card>}

        {user && <Card padding="lg" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <IconBadge tone="gold"><Banknote /></IconBadge>
              <h2 className="text-lg font-bold text-gray-800">Payout bank account</h2>
            </div>
            {bank?.has_details && !showBankForm && (
              <button
                onClick={() => setShowBankForm(true)}
                className="text-sm font-semibold text-emerald-700 hover:underline"
              >
                Update
              </button>
            )}
          </div>

          {bank?.has_details && !showBankForm ? (
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-1">
              <p className="font-semibold text-gray-800">{bank.bank}</p>
              <p className="text-sm text-gray-600">{maskedAccount(bank.account_number)} · {bank.account_holder}</p>
              <p className="text-xs text-gray-500">
                Branch {bank.branch_code} · {bank.account_type ? bank.account_type[0].toUpperCase() + bank.account_type.slice(1) : ''}
              </p>
              <p className="text-xs text-gray-400 pt-1">Withdrawals are paid to this account.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Set up the account we pay your winnings into. You only need to do this once.
              </p>
              <BankAccountForm
                onSaved={(d) => {
                  setBank(d);
                  setShowBankForm(false);
                }}
              />
              {bank?.has_details && (
                <button
                  onClick={() => setShowBankForm(false)}
                  className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </Card>}

        {user && <Card padding="lg" className="p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Social Links</h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Facebook</p>
              {isEditing ? (
                <input
                  type="url"
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-100 border-0 rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500"
                  placeholder="https://facebook.com/yourprofile"
                />
              ) : (
                <p className="text-sm text-gray-700 break-all">{user?.facebook_url || 'Not set'}</p>
              )}
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Instagram</p>
              {isEditing ? (
                <input
                  type="url"
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-100 border-0 rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500"
                  placeholder="https://instagram.com/yourprofile"
                />
              ) : (
                <p className="text-sm text-gray-700 break-all">{user?.instagram_url || 'Not set'}</p>
              )}
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">X</p>
              {isEditing ? (
                <input
                  type="url"
                  value={xUrl}
                  onChange={(e) => setXUrl(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-100 border-0 rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500"
                  placeholder="https://x.com/yourprofile"
                />
              ) : (
                <p className="text-sm text-gray-700 break-all">{user?.x_url || 'Not set'}</p>
              )}
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">TikTok</p>
              {isEditing ? (
                <input
                  type="url"
                  value={tiktokUrl}
                  onChange={(e) => setTiktokUrl(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-100 border-0 rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500"
                  placeholder="https://tiktok.com/@yourprofile"
                />
              ) : (
                <p className="text-sm text-gray-700 break-all">{user?.tiktok_url || 'Not set'}</p>
              )}
            </div>
          </div>
        </Card>}

        {user && <Card padding="lg" className="p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Account Status</h2>
          <div className="flex items-center space-x-2">
            <span className={`w-3 h-3 rounded-full ${user?.is_verified ? 'bg-green-500' : 'bg-amber-500'}`}></span>
            <span className="text-gray-700">
              {user?.is_verified ? 'Verified Account' : 'Pending Verification'}
            </span>
          </div>
        </Card>}

        {/* Save Button when Editing */}
        {isEditing && (
          <div className="space-y-3">
            {profileError && (
              <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {profileError}
              </p>
            )}
            <Button
              onClick={handleSave}
              isLoading={isSaving}
              fullWidth
              leftIcon={<Save className="h-5 w-5" />}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}

        {/* Logout Button */}
        {user && (
          <Button
            variant="danger"
            onClick={handleLogout}
            fullWidth
            leftIcon={<LogOut className="h-5 w-5" />}
          >
            Sign Out
          </Button>
        )}
      </div>
    </Layout>
  );
};

export default Profile;
